import { BookOpen, Target, Users, Zap, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Courses() {
  const courses = [
    {
      id: 1,
      category: 'Engineering Subjects',
      icon: BookOpen,
      color: 'blue',
      subjects: [
        'Computer Science (Data Structures, Algorithms, DBMS)',
        'Electronics & Communication (Analog, Digital, Communication)',
        'Electrical Engineering (Circuit Theory, Control Systems, Power Systems)',
        'Instrumentation (Measurement Systems, Process Control)',
        'Engineering Mathematics (Calculus, Linear Algebra, Differential Equations)',
      ],
    },
    {
      id: 2,
      category: 'GATE Preparation',
      icon: Target,
      color: 'purple',
      subjects: [
        'GATE - Computer Science',
        'GATE - Electronics & Communication',
        'GATE - Electrical Engineering',
        'GATE - Instrumentation Engineering',
        'Complete syllabus coverage',
        'Previous year question analysis',
        'Mock exams and assessments',
      ],
    },
    {
      id: 3,
      category: 'JEE Preparation',
      icon: Zap,
      color: 'orange',
      subjects: [
        'Physics (Mechanics, Thermodynamics, Electrodynamics, Optics, Modern Physics)',
        'Chemistry (Physical, Organic, Inorganic Chemistry)',
        'Mathematics (Algebra, Calculus, Coordinate Geometry, Trigonometry)',
        'JEE Main & Advanced Complete Preparation',
        'Topic-wise Problem Solving',
        'Previous Year Papers Practice',
      ],
    },
    {
      id: 4,
      category: 'NEET Preparation',
      icon: Heart,
      color: 'green',
      subjects: [
        'Physics (Mechanics, Heat, Optics, Electricity, Modern Physics)',
        'Chemistry (Physical, Organic, Inorganic Chemistry)',
        'Biology (Botany & Zoology)',
        'NCERT Based Complete Preparation',
        'Topic-wise MCQ Practice',
        'Mock Tests & Assessment',
      ],
    },
  ];

  const learningModes = [
    {
      mode: '1:1 Tutoring',
      icon: Users,
      features: ['Personalized attention', 'Flexible schedule', 'Custom learning pace', 'Direct interaction'],
    },
    {
      mode: 'Group Classes',
      icon: Zap,
      features: ['Collaborative learning', 'Cost-effective', 'Peer interaction', 'Regular assessments'],
    },
  ];

  return (
    <div>
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-brand-dark mb-6">Our Courses</h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive learning programs for Engineering, GATE, JEE & NEET success
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((course) => {
              const colorClasses = {
                blue: 'bg-blue-50 border-blue-200',
                purple: 'bg-purple-50 border-purple-200',
                orange: 'bg-orange-50 border-orange-200',
                green: 'bg-green-50 border-green-200',
              };
              const iconColorClasses = {
                blue: 'text-blue-600',
                purple: 'text-purple-600',
                orange: 'text-orange-600',
                green: 'text-green-600',
              };
              const dotColorClasses = {
                blue: 'bg-blue-600',
                purple: 'bg-purple-600',
                orange: 'bg-orange-600',
                green: 'bg-green-600',
              };
              
              return (
                <div 
                  key={course.id} 
                  data-testid={`course-category-${course.id}`} 
                  className={`p-8 rounded-2xl border-2 ${colorClasses[course.color]} transition-shadow hover:shadow-lg`}
                >
                  <course.icon className={`h-12 w-12 ${iconColorClasses[course.color]} mb-4`} />
                  <h2 className="text-2xl md:text-3xl font-semibold text-brand-dark mb-6 font-display">{course.category}</h2>
                  <ul className="space-y-3">
                    {course.subjects.map((subject, idx) => (
                      <li key={idx} className="flex items-start text-slate-700">
                        <span className={`inline-block w-2 h-2 ${dotColorClasses[course.color]} rounded-full mt-2 mr-3 flex-shrink-0`}></span>
                        <span>{subject}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4">Learning Modes</h2>
            <p className="text-lg text-slate-600">Choose what works best for you</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {learningModes.map((mode, idx) => (
              <div key={idx} data-testid={`learning-mode-${idx}`} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <mode.icon className="h-12 w-12 text-brand-blue mb-4" />
                <h3 className="text-2xl font-semibold text-brand-dark mb-4 font-display">{mode.mode}</h3>
                <ul className="space-y-3">
                  {mode.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center text-slate-600">
                      <span className="inline-block w-2 h-2 bg-brand-blue rounded-full mr-3"></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-6">Can't Find Your Course?</h2>
          <p className="text-lg text-slate-600 mb-8">
            We offer customized courses based on your specific needs. Contact us to discuss your requirements.
          </p>
          <Link to="/enroll">
            <Button data-testid="custom-course-btn" size="lg" className="btn-primary">
              Request Custom Course
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
